import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** Recharts tooltip styling that follows the theme tokens. */
export const chartTooltipProps = {
  contentStyle: {
    background: "var(--popover)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    color: "var(--popover-foreground)",
    fontSize: 12,
    boxShadow: "var(--shadow-elevated)",
  },
  labelStyle: { color: "var(--muted-foreground)", marginBottom: 4 },
  itemStyle: { color: "var(--popover-foreground)" },
  cursor: { fill: "var(--muted)" },
} as const;

export const chartAxisTick = { fontSize: 11, fill: "var(--muted-foreground)" } as const;

/** Square avatar with initial fallback, as used for people/orgs in tables. */
export function PeopleAvatar({
  src,
  name,
  className,
}: {
  src?: string | null;
  name?: string | null;
  className?: string;
}) {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <Avatar className={cn("size-8 rounded-sm", className)}>
      {src ? <AvatarImage src={src} alt="" /> : null}
      <AvatarFallback className="rounded-full">{initial}</AvatarFallback>
    </Avatar>
  );
}

/** Label/value pair for detail panels. */
export function PeopleDetailRow({
  icon,
  label,
  value,
  className,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 items-start gap-3 py-2.5", className)}>
      {icon && (
        <span className="mt-0.5 text-muted-foreground [&_svg]:size-4" aria-hidden>
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="eyebrow">{label}</p>
        <div className="mt-0.5 text-sm break-words text-foreground">{value}</div>
      </div>
    </div>
  );
}

/**
 * Row action that keeps a tooltip working while the button is disabled
 * (e.g. "Log in as…" on records with no linked account).
 */
export function PeopleTooltipAction({
  label,
  tooltip,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  tooltip: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={disabled ? 0 : undefined} className="inline-flex rounded-sm">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className="text-muted-foreground hover:text-foreground"
          >
            {icon}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
